/**
 * RecipesPage — Recipe library (the "Favorites" from the personal app).
 * Matches the prototype's FavoritesPage layout:
 *   - Meal-card tile grid with prominent photos
 *   - Click card to view full recipe in modal
 *   - Edit / delete buttons on each card
 *   - Three methods to add: Photo scan, URL import, Manual entry
 *   - Two-step AI flow: extract → review in RecipeEditForm → save
 */
import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Camera, Globe, PenLine, Search, X, Loader, ShoppingCart,
} from 'lucide-react';
import { useApi, ApiError } from '../../hooks/useApi';
import { useAuth } from '../../context/AuthContext';
import RecipeEditForm from '../../components/recipe/RecipeEditForm';
import RecipeModal from '../../components/recipe/RecipeModal';
import OnboardingTooltip from '../../components/common/OnboardingTooltip';
import '../../styles/recipes.css';

// ---- Types ----
interface Recipe {
  recipeId: string;
  title: string;
  image?: string;
  readyInMinutes?: number;
  servings?: number;
  sourceUrl?: string;
  summary?: string;
  cuisines?: string[];
  dishTypes?: string[];
  diets?: string[];
  ingredients?: Ingredient[];
  instructions?: Instruction[];
  source?: string;
  createdBy?: string;
  createdAt?: string;
  candidateImages?: string[];
  confidence?: number;
}

interface Ingredient {
  name: string;
  amount?: number;
  unit?: string;
  original?: string;
  aisle?: string;
}

interface Instruction {
  number?: number;
  step: string;
}

type AddMode = null | 'scan' | 'url' | 'manual' | 'edit';

const RecipesPage: React.FC = () => {
  const apiRef = useRef(useApi());
  const { user } = useAuth();
  const isPrimary = user?.role === 'primary';

  const [recipes, setRecipes] = useState<Recipe[]>([]);
  const [loading, setLoading] = useState(true);
  const [addMode, setAddMode] = useState<AddMode>(null);
  const [searchQuery, setSearchQuery] = useState('');

  // AI extraction state
  const [aiExtractedData, setAiExtractedData] = useState<Partial<Recipe> | null>(null);

  // Edit state
  const [editingRecipe, setEditingRecipe] = useState<Recipe | null>(null);

  // Modal state
  const [selectedRecipe, setSelectedRecipe] = useState<Recipe | null>(null);

  // Ingredient quick-view state
  const [ingredientViewRecipe, setIngredientViewRecipe] = useState<Recipe | null>(null);

  // Toast
  const [toast, setToast] = useState<string | null>(null);
  const showToast = useCallback((message: string) => {
    setToast(message);
  }, []);

  // Load recipes from API
  const loadRecipes = useCallback(async () => {
    if (!user?.householdId) {
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const data = await apiRef.current.get<{ recipes?: Recipe[] } | Recipe[]>(
        `/v1/households/${user.householdId}/recipes`
      );
      // Handle both { recipes: [...] } and [...] response shapes
      const list = Array.isArray(data) ? data : (data?.recipes ?? []);
      setRecipes(list);
    } catch (err) {
      console.error('Failed to load recipes:', err);
      showToast('Failed to load recipes');
    } finally {
      setLoading(false);
    }
  }, [user?.householdId, showToast]);

  useEffect(() => {
    loadRecipes();
  }, [loadRecipes]);

  // Delete recipe
  const handleDelete = async (recipeId: string) => {
    if (!window.confirm('Remove this recipe?')) return;
    if (!user?.householdId) return;
    try {
      await apiRef.current.del(
        `/v1/households/${user.householdId}/recipes/${recipeId}`
      );
      setRecipes((prev) => prev.filter((r) => r.recipeId !== recipeId));
      showToast('Recipe removed');
    } catch (err) {
      console.error(err);
      showToast('Failed to remove recipe');
    }
  };

  // Open edit mode
  const openEdit = (recipe: Recipe) => {
    setEditingRecipe(recipe);
    setAiExtractedData(null);
    setAddMode('edit');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Close panel
  const closePanel = () => {
    setAddMode(null);
    setEditingRecipe(null);
    setAiExtractedData(null);
  };

  // After form saved
  const handleFormSaved = () => {
    closePanel();
    loadRecipes();
  };

  // Toggle add mode
  const toggleAddMode = (mode: AddMode) => {
    if (addMode === mode) {
      closePanel();
    } else {
      setAddMode(mode);
      setEditingRecipe(null);
      setAiExtractedData(null);
    }
  };

  // Filter recipes by search
  const filteredRecipes = recipes.filter((r) =>
    r.title.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const panelOpen = addMode !== null;

  if (loading) {
    return (
      <div className="loading-container">
        <div className="spinner" />
        <p>Loading recipes...</p>
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>Recipes</h1>
        <p>{recipes.length} saved recipe{recipes.length !== 1 ? 's' : ''}</p>
      </div>

      {/* ---- Add Recipe Buttons ---- */}
      <div className="add-recipe-bar">
        <span className="add-recipe-label">Add a recipe:</span>
        <div className="add-recipe-buttons">
          {([
            { id: 'scan' as const, icon: <Camera size={14} />, label: 'Scan Photo' },
            { id: 'url' as const, icon: <Globe size={14} />, label: 'Import Website URL' },
            { id: 'manual' as const, icon: <PenLine size={14} />, label: 'Manual' },
          ]).map((btn) => (
            <button
              key={btn.id}
              className={`btn ${addMode === btn.id ? 'btn-primary' : 'btn-secondary'}`}
              onClick={() => toggleAddMode(btn.id)}
              style={{ fontSize: '0.85rem' }}
            >
              {btn.icon} {btn.label}
            </button>
          ))}
        </div>
      </div>

      {/* ---- Search ---- */}
      {recipes.length > 0 && (
        <div className="recipes-search">
          <Search size={16} />
          <input
            type="text"
            placeholder="Search recipes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
          />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              style={{ background: 'none', border: 'none', cursor: 'pointer', color: 'var(--color-text-muted)' }}
            >
              <X size={16} />
            </button>
          )}
        </div>
      )}

      {/* First-time tips */}
      <OnboardingTooltip
        id="recipe-import"
        message="Add recipes by scanning a photo, pasting a website URL, or typing manually. AI extracts ingredients and instructions automatically — you can always edit before saving."
      />
      {recipes.length > 0 && (
        <OnboardingTooltip
          id="recipe-shopping-cart"
          message="Tap the 🛒 icon on any recipe card to see a quick ingredient checklist — handy when you're at the store and want to check what you need."
          icon="🛒"
        />
      )}

      {/* ---- Expandable Panel ---- */}
      {panelOpen && (
        <div className="add-recipe-panel">
          <button className="add-panel-close" onClick={closePanel}>✕ Close</button>

          {/* Manual Entry */}
          {addMode === 'manual' && (
            <RecipeEditForm
              showToast={showToast}
              onSave={handleFormSaved}
              onCancel={closePanel}
            />
          )}

          {/* Edit Existing */}
          {addMode === 'edit' && editingRecipe && (
            <RecipeEditForm
              initialData={editingRecipe}
              isEdit
              showToast={showToast}
              onSave={handleFormSaved}
              onCancel={closePanel}
            />
          )}

          {/* Scan Photo — Step 1: upload */}
          {addMode === 'scan' && !aiExtractedData && (
            <PhotoScanStep
              showToast={showToast}
              onExtracted={(data) => setAiExtractedData(data)}
            />
          )}
          {/* Scan Photo — Step 2: review & edit */}
          {addMode === 'scan' && aiExtractedData && (
            <div>
              <div className="ai-success-banner">
                ✓ AI extracted "<strong>{aiExtractedData.title}</strong>" — review and edit below, then save.
              </div>
              {(aiExtractedData.confidence != null && aiExtractedData.confidence < 70) && (
                <div className="ai-confidence-warning" style={{
                  padding: '12px 16px', marginBottom: '16px',
                  backgroundColor: '#FEF3C7', border: '1px solid #F59E0B',
                  borderRadius: '8px', fontSize: '0.9rem', color: '#92400E',
                  lineHeight: 1.5,
                }}>
                  ⚠️ <strong>Low confidence extraction</strong> — The AI had difficulty reading this recipe
                  (confidence: {aiExtractedData.confidence}%). Please check the title, ingredients, and
                  instructions carefully. <strong>Tip:</strong> For handwritten recipes, use good lighting,
                  avoid shadows, and ensure the full recipe is visible in the photo.
                </div>
              )}
              <RecipeEditForm
                initialData={aiExtractedData}
                showToast={showToast}
                onSave={handleFormSaved}
                onCancel={() => setAiExtractedData(null)}
              />
            </div>
          )}

          {/* Import URL — Step 1: paste URL */}
          {addMode === 'url' && !aiExtractedData && (
            <UrlImportStep
              showToast={showToast}
              onExtracted={(data) => setAiExtractedData(data)}
            />
          )}
          {/* Import URL — Step 2: review & edit */}
          {addMode === 'url' && aiExtractedData && (
            <div>
              <div className="ai-success-banner">
                ✓ AI extracted "<strong>{aiExtractedData.title}</strong>" — review and edit below, then save.
              </div>
              {(aiExtractedData.confidence != null && aiExtractedData.confidence < 70) && (
                <div className="ai-confidence-warning" style={{
                  padding: '12px 16px', marginBottom: '16px',
                  backgroundColor: '#FEF3C7', border: '1px solid #F59E0B',
                  borderRadius: '8px', fontSize: '0.9rem', color: '#92400E',
                  lineHeight: 1.5,
                }}>
                  ⚠️ <strong>Low confidence extraction</strong> — The AI had difficulty extracting this
                  recipe (confidence: {aiExtractedData.confidence}%). Please check the title, ingredients,
                  and instructions carefully before saving.
                </div>
              )}
              <RecipeEditForm
                initialData={aiExtractedData}
                showToast={showToast}
                onSave={handleFormSaved}
                onCancel={() => setAiExtractedData(null)}
              />
            </div>
          )}
        </div>
      )}

      {/* ---- Recipe Grid ---- */}
      {filteredRecipes.length > 0 ? (
        <div className="meals-grid" style={{ marginTop: panelOpen ? '24px' : '0' }}>
          {filteredRecipes.map((recipe) => (
            <div key={recipe.recipeId} className="meal-card">
              <div
                className="meal-card-image"
                onClick={() => setSelectedRecipe(recipe)}
                style={{ cursor: 'pointer' }}
              >
                {recipe.image ? (
                  <img src={recipe.image} alt={recipe.title} loading="lazy" />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'var(--color-bg-alt)',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '2.5rem',
                    }}
                  >
                    🍽️
                  </div>
                )}
                {recipe.source && (
                  <span className="meal-card-source">
                    {recipe.source === 'claude-photo' && '📷 Photo'}
                    {recipe.source === 'claude-url' && '🔗 URL'}
                    {recipe.source === 'manual' && '✏️ Manual'}
                    {recipe.source === 'import' && '📥 Import'}
                  </span>
                )}
              </div>
              <div className="meal-card-body">
                <h3
                  className="meal-card-title"
                  onClick={() => setSelectedRecipe(recipe)}
                  style={{ cursor: 'pointer' }}
                >
                  {recipe.title}
                </h3>
                <div className="meal-card-meta">
                  {(recipe.readyInMinutes ?? 0) > 0 && (
                    <span>⏱ {recipe.readyInMinutes} min</span>
                  )}
                  {(recipe.servings ?? 0) > 0 && (
                    <span>👥 {recipe.servings} servings</span>
                  )}
                </div>
                {recipe.cuisines && recipe.cuisines.length > 0 && (
                  <div className="meal-card-meta">
                    <span>🌍 {recipe.cuisines.join(', ')}</span>
                  </div>
                )}
                <div style={{ display: 'flex', gap: '6px', marginTop: '8px' }}>
                  <button
                    className="btn btn-secondary"
                    style={{ padding: '6px 10px', fontSize: '0.8rem', justifyContent: 'center' }}
                    onClick={() => setIngredientViewRecipe(recipe)}
                    title="Quick ingredient list for shopping"
                  >
                    <ShoppingCart size={13} />
                  </button>
                  <button
                    className="btn btn-secondary"
                    style={{ flex: 1, padding: '6px', fontSize: '0.8rem', justifyContent: 'center' }}
                    onClick={() => openEdit(recipe)}
                  >
                    ✏️ Edit
                  </button>
                  {isPrimary && (
                    <button
                      className="btn btn-secondary"
                      style={{ padding: '6px 10px', fontSize: '0.8rem', color: 'var(--color-error)' }}
                      onClick={() => handleDelete(recipe.recipeId)}
                    >
                      🗑️
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : !panelOpen ? (
        <div className="favorites-empty">
          <span style={{ fontSize: '3rem' }}>📖</span>
          <h3>No recipes yet</h3>
          <p>Use the buttons above to start adding recipes — scan a photo, import a URL, or add manually.</p>
        </div>
      ) : null}

      {/* ---- Recipe Modal ---- */}
      {selectedRecipe && (
        <RecipeModal
          recipe={selectedRecipe}
          onClose={() => setSelectedRecipe(null)}
          onEdit={(recipe) => {
            setSelectedRecipe(null);
            openEdit(recipe);
          }}
          onDelete={isPrimary ? (recipeId) => {
            setSelectedRecipe(null);
            handleDelete(recipeId);
          } : undefined}
        />
      )}

      {/* ---- Ingredient Quick-View Modal ---- */}
      {ingredientViewRecipe && (
        <IngredientQuickView
          recipe={ingredientViewRecipe}
          onClose={() => setIngredientViewRecipe(null)}
        />
      )}

      {/* ---- Toast ---- */}
      {toast && <div className="toast" onClick={() => setToast(null)} style={{ cursor: 'pointer' }}>{toast}</div>}
    </div>
  );
};

export default RecipesPage;


/* ================================================
   Image/PDF → JPEG converter
   Ported from the personal app's FavoritesPage.js.
   Draws onto canvas, caps at 2000px max dimension,
   outputs JPEG at 0.85 quality — keeps base64 payload
   well under API Gateway's ~10MB limit.
   ================================================ */
function convertFileToJpeg(file: File): Promise<{ base64: string; mimeType: string }> {
  return new Promise((resolve, reject) => {
    const isPdf = file.type === 'application/pdf' || file.name.toLowerCase().endsWith('.pdf');

    if (isPdf) {
      // Load pdf.js from CDN, render page 1 to canvas
      const script = document.createElement('script');
      script.src = 'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.min.js';
      script.onload = async () => {
        try {
          const pdfjsLib = (window as any)['pdfjs-dist/build/pdf'];
          pdfjsLib.GlobalWorkerOptions.workerSrc =
            'https://cdnjs.cloudflare.com/ajax/libs/pdf.js/3.11.174/pdf.worker.min.js';

          const arrayBuffer = await file.arrayBuffer();
          const pdf = await pdfjsLib.getDocument({ data: arrayBuffer }).promise;
          const page = await pdf.getPage(1);
          const viewport = page.getViewport({ scale: 2.0 });

          const canvas = document.createElement('canvas');
          canvas.width = viewport.width;
          canvas.height = viewport.height;
          const ctx = canvas.getContext('2d')!;
          await page.render({ canvasContext: ctx, viewport }).promise;

          // Limit to 2000px max dimension
          const maxDim = 2000;
          if (canvas.width > maxDim || canvas.height > maxDim) {
            const scale = maxDim / Math.max(canvas.width, canvas.height);
            const small = document.createElement('canvas');
            small.width = Math.round(canvas.width * scale);
            small.height = Math.round(canvas.height * scale);
            small.getContext('2d')!.drawImage(canvas, 0, 0, small.width, small.height);
            const dataUrl = small.toDataURL('image/jpeg', 0.9);
            resolve({ base64: dataUrl.split(',')[1], mimeType: 'image/jpeg' });
          } else {
            const dataUrl = canvas.toDataURL('image/jpeg', 0.9);
            resolve({ base64: dataUrl.split(',')[1], mimeType: 'image/jpeg' });
          }
        } catch (err) {
          reject(new Error('Failed to read PDF: ' + (err instanceof Error ? err.message : String(err))));
        }
      };
      script.onerror = () => reject(new Error('Failed to load PDF library'));
      if (!(window as any)['pdfjs-dist/build/pdf']) {
        document.head.appendChild(script);
      } else {
        script.onload!(new Event('load'));
      }
      return;
    }

    // For all image types (HEIC, JPG, PNG, WebP, GIF):
    // The browser's Image element handles decoding.
    // Safari on iOS/Mac can decode HEIC natively.
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      let w = img.width;
      let h = img.height;

      // Limit to 2000px max to keep payload reasonable
      const maxDim = 2000;
      if (w > maxDim || h > maxDim) {
        const scale = maxDim / Math.max(w, h);
        w = Math.round(w * scale);
        h = Math.round(h * scale);
      }

      canvas.width = w;
      canvas.height = h;
      const ctx = canvas.getContext('2d')!;
      ctx.drawImage(img, 0, 0, w, h);
      const dataUrl = canvas.toDataURL('image/jpeg', 0.85);
      resolve({ base64: dataUrl.split(',')[1], mimeType: 'image/jpeg' });
    };
    img.onerror = () => reject(new Error('Could not decode image. If this is a HEIC file from a non-Apple device, try converting it to JPG first.'));
    img.src = URL.createObjectURL(file);
  });
}


/* ================================================
   Photo Scan Step
   Accepts: JPG, HEIC, PNG, WebP, GIF, PDF
   Converts everything to JPEG via canvas before sending
   ================================================ */
interface PhotoScanStepProps {
  showToast: (msg: string) => void;
  onExtracted: (data: Partial<Recipe>) => void;
}

function PhotoScanStep({ showToast, onExtracted }: PhotoScanStepProps) {
  const apiRef = useRef(useApi());
  const { user } = useAuth();
  const [file, setFile] = useState<File | null>(null);
  const [preview, setPreview] = useState<string | null>(null);
  const [analyzing, setAnalyzing] = useState(false);

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selected = e.target.files?.[0];
    if (!selected) return;

    const isImage = selected.type.startsWith('image/') || /\.(heic|heif)$/i.test(selected.name);
    const isPdf = selected.type === 'application/pdf' || selected.name.toLowerCase().endsWith('.pdf');

    if (!isImage && !isPdf) {
      showToast('Please select an image (JPG, HEIC, PNG) or PDF file');
      return;
    }
    if (selected.size > 20 * 1024 * 1024) {
      showToast('File must be under 20MB');
      return;
    }

    setFile(selected);

    if (isPdf) {
      setPreview('pdf');
    } else {
      setPreview(URL.createObjectURL(selected));
    }
  };

  const handleAnalyze = async () => {
    if (!file || !user?.householdId) return;
    setAnalyzing(true);
    try {
      // Convert to compressed JPEG via canvas (handles HEIC, PDF, large images)
      showToast('Converting file...');
      const { base64, mimeType } = await convertFileToJpeg(file);

      showToast('Sending to AI for extraction...');
      const data = await apiRef.current.post<Partial<Recipe> & { recipe?: Partial<Recipe> }>(
        `/v1/households/${user.householdId}/recipes/analyze`,
        { type: 'photo', imageData: base64, imageMimeType: mimeType }
      );

      // Handle both wrapped { recipe: {...} } and flat { title: ... } response shapes
      const recipe = data?.recipe ?? (data?.title ? data : null);
      if (recipe) {
        showToast(`Extracted "${recipe.title}" — review below`);
        onExtracted(recipe);
      } else {
        showToast('Could not extract a recipe from this image');
      }
    } catch (err) {
      console.error(err);
      const message = err instanceof ApiError ? err.message
        : err instanceof Error ? err.message
        : 'Failed to analyze. Make sure the file contains a readable recipe.';
      showToast(message);
    } finally {
      setAnalyzing(false);
    }
  };

  const fileName = file ? file.name : '';
  const fileSize = file ? (file.size / 1024 / 1024).toFixed(1) + ' MB' : '';

  return (
    <div style={{ maxWidth: '700px' }}>
      <h3 style={{ fontWeight: 700, marginBottom: '8px' }}>📷 Scan a Recipe</h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginBottom: '8px' }}>
        Upload a photo (JPG, HEIC, PNG) or PDF of a recipe. AI extracts the recipe — you'll review before saving.
      </p>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.82rem', marginBottom: '16px', fontStyle: 'italic' }}>
        💡 For handwritten recipes: use good lighting, avoid shadows, and make sure the entire recipe
        is visible. The AI may need you to correct some items — the edit screen makes this easy.
      </p>

      <div
        className="image-dropzone"
        onClick={() => document.getElementById('scan-input')?.click()}
        style={{ marginBottom: '16px' }}
      >
        {preview === 'pdf' ? (
          <div className="dropzone-placeholder">
            <span style={{ fontSize: '2.5rem' }}>📄</span>
            <span style={{ fontWeight: 600 }}>{fileName}</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>{fileSize}</span>
          </div>
        ) : preview ? (
          <img src={preview} alt="Preview" className="image-preview" />
        ) : (
          <div className="dropzone-placeholder">
            <span style={{ fontSize: '2.5rem' }}>📷</span>
            <span>Click to select a photo or PDF</span>
            <span style={{ fontSize: '0.78rem', color: 'var(--color-text-muted)' }}>
              JPG, HEIC, PNG, WebP, GIF, PDF — up to 20MB
            </span>
          </div>
        )}
      </div>
      <input
        id="scan-input"
        type="file"
        accept="image/jpeg,image/png,image/gif,image/webp,image/heic,image/heif,application/pdf"
        onChange={handleFileSelect}
        style={{ display: 'none' }}
      />

      {file && (
        <div style={{ marginBottom: '12px', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
          Selected: <strong>{fileName}</strong> ({fileSize})
        </div>
      )}

      <button
        className="btn btn-primary"
        onClick={handleAnalyze}
        disabled={!file || analyzing}
        style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
      >
        {analyzing ? '🤖 AI is analyzing...' : '🤖 Extract Recipe with AI'}
      </button>

      {analyzing && (
        <div style={{ marginTop: '12px', textAlign: 'center' }}>
          <div className="spinner" style={{ margin: '0 auto' }} />
          <p style={{ fontSize: '0.85rem', color: 'var(--color-text-muted)', marginTop: '8px' }}>
            This may take 10–15 seconds...
          </p>
        </div>
      )}
    </div>
  );
}


/* ================================================
   URL Import Step — with automatic retry
   3 attempts: immediate → wait 5s → wait 15s
   ================================================ */
interface UrlImportStepProps {
  showToast: (msg: string) => void;
  onExtracted: (data: Partial<Recipe>) => void;
}

function UrlImportStep({ showToast, onExtracted }: UrlImportStepProps) {
  const apiRef = useRef(useApi());
  const { user } = useAuth();
  const [url, setUrl] = useState('');
  const [analyzing, setAnalyzing] = useState(false);
  const [statusMsg, setStatusMsg] = useState('');
  const [importFailed, setImportFailed] = useState(false);

  const handleImport = async () => {
    if (!url.trim()) {
      showToast('Please enter a URL');
      return;
    }
    if (!user?.householdId) {
      showToast('No household found');
      return;
    }

    setAnalyzing(true);
    setStatusMsg('');
    setImportFailed(false);

    const maxAttempts = 3;
    const delays = [5000, 15000]; // wait before retry 2 and 3

    for (let attempt = 1; attempt <= maxAttempts; attempt++) {
      try {
        setStatusMsg(
          attempt === 1
            ? 'Fetching recipe from URL...'
            : `Attempt ${attempt} of ${maxAttempts} — retrying...`
        );

        const data = await apiRef.current.post<Partial<Recipe> & { recipe?: Partial<Recipe>; candidateImages?: string[] }>(
          `/v1/households/${user.householdId}/recipes/analyze`,
          { type: 'url', url: url.trim() }
        );

        // Handle both wrapped { recipe: {...} } and flat { title: ... } response shapes
        const recipe = data?.recipe ?? (data?.title ? data : null);
        if (recipe) {
          // Pass candidateImages through to the edit form
          if (data?.candidateImages) {
            (recipe as Record<string, unknown>).candidateImages = data.candidateImages;
          }
          showToast(`Extracted "${recipe.title}" — review below`);
          onExtracted({ ...recipe, sourceUrl: url.trim() });
          setAnalyzing(false);
          setStatusMsg('');
          return; // success — exit
        } else {
          throw new Error('Could not extract a recipe from this URL');
        }
      } catch (err) {
        console.error(`URL import attempt ${attempt} failed:`, err);

        // Non-retryable errors — show message immediately, don't retry
        if (err instanceof ApiError) {
          const noRetry = ['RECIPE_INVALID_URL', 'RECIPE_PAGE_NOT_FOUND', 'RECIPE_SITE_BLOCKED',
            'RECIPE_URL_TOO_LARGE', 'RECIPE_NOT_FOUND', 'RATE_LIMIT'];
          if (noRetry.includes(err.code)) {
            setStatusMsg('');
            setAnalyzing(false);
            if (err.code !== 'RATE_LIMIT') setImportFailed(true);
            showToast(err.message);
            return;
          }
        }

        if (attempt < maxAttempts) {
          const delaySec = delays[attempt - 1] / 1000;
          setStatusMsg(`Attempt ${attempt} didn't work — retrying in ${delaySec} seconds...`);
          await new Promise((resolve) => setTimeout(resolve, delays[attempt - 1]));
        } else {
          // All attempts exhausted
          setStatusMsg('');
          const message = err instanceof ApiError ? err.message
            : err instanceof Error ? err.message : '';
          setImportFailed(true);
          showToast(message || 'Could not import this recipe. Please try again later.');
        }
      }
    }

    setAnalyzing(false);
  };

  return (
    <div style={{ maxWidth: '700px' }}>
      <h3 style={{ fontWeight: 700, marginBottom: '8px' }}>🔗 Import from URL</h3>
      <p style={{ color: 'var(--color-text-muted)', fontSize: '0.9rem', marginBottom: '16px' }}>
        Paste a URL from any recipe website. AI extracts the recipe — you'll review before saving.
      </p>
      <div style={{ marginBottom: '16px' }}>
        <input
          type="url"
          value={url}
          onChange={(e) => setUrl(e.target.value)}
          placeholder="https://www.allrecipes.com/recipe/..."
          onKeyDown={(e) => e.key === 'Enter' && !analyzing && handleImport()}
          disabled={analyzing}
        />
        {importFailed && !analyzing && (
          <div style={{
            marginTop: '10px',
            padding: '10px 12px',
            fontSize: '0.85rem',
            lineHeight: 1.45,
            color: 'var(--color-text-muted)',
            background: 'var(--color-bg-alt, #f9fafb)',
            border: '1px solid var(--color-border, #e5e7eb)',
            borderRadius: '6px',
          }}>
            💡 Some websites block automated access. Try again in a moment, or print the recipe page to a PDF and upload it using <strong>Scan Photo</strong>.
          </div>
        )}
      </div>
      <button
        className="btn btn-primary"
        onClick={handleImport}
        disabled={analyzing || !url.trim()}
        style={{ width: '100%', justifyContent: 'center', padding: '14px' }}
      >
        {analyzing ? (
          <><Loader size={16} className="spin" /> Importing...</>
        ) : (
          <><Globe size={16} /> Import Recipe</>
        )}
      </button>
      {statusMsg && (
        <p style={{
          marginTop: '12px', textAlign: 'center',
          fontSize: '0.85rem', color: 'var(--color-text-muted)',
        }}>
          {statusMsg}
        </p>
      )}
    </div>
  );
}


/* ================================================
   Ingredient Quick-View — lightweight checklist
   for looking up a recipe's ingredients at the store
   ================================================ */
interface IngredientQuickViewProps {
  recipe: Recipe;
  onClose: () => void;
}

function IngredientQuickView({ recipe, onClose }: IngredientQuickViewProps) {
  const [checked, setChecked] = useState<Record<number, boolean>>({});
  const ingredients = recipe.ingredients || [];

  const toggleCheck = (idx: number) => {
    setChecked((prev) => ({ ...prev, [idx]: !prev[idx] }));
  };

  const checkedCount = Object.values(checked).filter(Boolean).length;

  const copyIngredients = async () => {
    const lines = ingredients.map((ing) => {
      const qty = ing.amount ? `${ing.amount}` : '';
      const unit = ing.unit || '';
      const name = ing.name.charAt(0).toUpperCase() + ing.name.slice(1);
      return `- ${[qty, unit, name].filter(Boolean).join(' ')}`;
    });
    const text = `🛒 ${recipe.title}\n\n${lines.join('\n')}`;
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const ta = document.createElement('textarea');
      ta.value = text;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
    }
  };

  return (
    <div className="recipe-modal-overlay" onClick={onClose}>
      <div
        className="recipe-modal"
        onClick={(e) => e.stopPropagation()}
        style={{ maxWidth: 460, maxHeight: '80vh', overflow: 'auto' }}
      >
        <div style={{ padding: '20px 24px 12px' }}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: '12px' }}>
            <div>
              <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 700 }}>
                <ShoppingCart size={16} style={{ marginRight: '6px', verticalAlign: '-2px' }} />
                {recipe.title}
              </h3>
              <p style={{ margin: '4px 0 0', fontSize: '0.82rem', color: 'var(--color-text-muted)' }}>
                {checkedCount} of {ingredients.length} items checked
              </p>
            </div>
            <button
              onClick={onClose}
              style={{ background: 'none', border: 'none', cursor: 'pointer', fontSize: '1.2rem', color: 'var(--color-text-muted)', padding: '4px' }}
            >
              ✕
            </button>
          </div>
          <div style={{ display: 'flex', gap: '8px', marginBottom: '16px' }}>
            <button className="btn btn-secondary" onClick={copyIngredients} style={{ fontSize: '0.8rem', padding: '6px 12px' }}>
              📋 Copy List
            </button>
          </div>
        </div>
        <div style={{ padding: '0 24px 20px' }}>
          {ingredients.length === 0 ? (
            <p style={{ color: 'var(--color-text-muted)', textAlign: 'center' }}>No ingredients listed.</p>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '4px' }}>
              {ingredients.map((ing, idx) => {
                const isChecked = !!checked[idx];
                const qty = ing.amount ? `${ing.amount}` : '';
                const unit = ing.unit || '';
                const name = ing.name.charAt(0).toUpperCase() + ing.name.slice(1);
                const display = [qty, unit, name].filter(Boolean).join(' ');
                return (
                  <div
                    key={idx}
                    onClick={() => toggleCheck(idx)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: '10px',
                      padding: '8px 10px', borderRadius: '6px', cursor: 'pointer',
                      backgroundColor: isChecked ? 'var(--color-bg-alt, #f3f4f6)' : 'transparent',
                      transition: 'background-color 0.15s',
                    }}
                  >
                    <span style={{
                      width: '22px', height: '22px', borderRadius: '4px',
                      border: isChecked ? 'none' : '2px solid var(--color-border, #d1d5db)',
                      backgroundColor: isChecked ? 'var(--color-primary, #E07A5F)' : 'transparent',
                      display: 'flex', alignItems: 'center', justifyContent: 'center',
                      flexShrink: 0, fontSize: '14px', color: '#fff',
                    }}>
                      {isChecked && '✓'}
                    </span>
                    <span style={{
                      fontSize: '0.9rem',
                      textDecoration: isChecked ? 'line-through' : 'none',
                      color: isChecked ? 'var(--color-text-muted)' : 'var(--color-text)',
                    }}>
                      {display}
                    </span>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
